/**
 * ClosuresGrid — FL-Übersicht abgeschlossener Periodenabschlüsse als Kachel-Grid.
 * Klick auf Karte öffnet Detail-Modal mit Korrekturen, Anmerkungen & Anlagen.
 */
import React, { useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, Download, Loader2, FileText, Edit3, Paperclip, Calendar, X, MessageSquare, ChevronRight } from 'lucide-react';

const fmtEur = (n) => new Intl.NumberFormat('de-DE',
  { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }
).format(Number(n) || 0);

const periodLabel = (c) => {
  const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  if (c.period_type === 'year') return `${c.year}`;
  if (c.period_type === 'quarter') return `Q${c.period_value} ${c.year}`;
  if (c.period_type === 'month') return `${months[(c.period_value || 1) - 1]} ${c.year}`;
  return `${c.year}`;
};

export default function ClosuresGrid() {
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/closures');
        if (alive) setClosures(r.data?.closures || []);
      } catch {
        if (alive) toast.error('Abschlüsse konnten nicht geladen werden');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleDownload = async (closure) => {
    setDownloadingId(closure.id);
    try {
      const r = await api.get(`/closures/${closure.id}/report`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Abschluss_${periodLabel(closure).replace(' ', '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download fehlgeschlagen');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-slate-500" data-testid="closures-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />Abschlüsse laden...
      </div>
    );
  }

  if (closures.length === 0) {
    return (
      <Card data-testid="closures-empty">
        <CardContent className="p-10 text-center">
          <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-7 h-7 text-emerald-600" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1">Noch keine Abschlüsse</h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            Sobald dein Steuerberater einen Quartals- oder Monatsabschluss festgeschrieben hat,
            erscheinen hier die Berichte als Karten — inkl. PDF-Download.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="closures-grid">
      {closures.map((c) => {
        const k = c.kpis || {};
        const finalizedAt = c.finalized_at ? new Date(c.finalized_at) : null;
        const correctionCount = (c.corrections || []).length;
        const attachmentCount = (c.attachment_doc_ids || []).length;
        return (
          <Card key={c.id}
                onClick={() => setSelectedId(c.id)}
                className="hover:shadow-md transition-all cursor-pointer"
                data-testid={`closure-card-${c.id}`}>
            <CardContent className="p-5">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-lg font-bold text-slate-900 tracking-tight">
                      {periodLabel(c)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Festgeschrieben {finalizedAt?.toLocaleDateString('de-DE') || '—'}
                    {c.advisor_name && ` · ${c.advisor_name}`}
                  </p>
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                  Festgeschrieben
                </span>
              </div>

              {/* KPI grid */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Kpi label="Einnahmen netto" value={k.income_netto} accent="emerald" />
                <Kpi label="Ausgaben netto" value={k.expense_netto} accent="slate" />
                <Kpi label="Gewinn netto"   value={k.profit_netto} accent="emerald" emphasize />
                <Kpi label="USt-Saldo"      value={k.vat_balance} accent={Number(k.vat_balance) >= 0 ? 'amber' : 'emerald'} />
              </div>

              {/* Meta + Action */}
              <div className="flex items-center justify-between border-t border-zinc-100 pt-3">
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="flex items-center gap-1" title="Korrekturen durch SB">
                    <Edit3 className="w-3 h-3" />
                    {correctionCount}
                  </span>
                  <span className="flex items-center gap-1" title="Verknüpfte Anlagen">
                    <Paperclip className="w-3 h-3" />
                    {attachmentCount}
                  </span>
                  <span className="text-slate-400">
                    {k.invoice_count || 0} R · {k.expense_count || 0} A
                  </span>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <span className="text-xs text-slate-400 flex items-center gap-0.5">Details<ChevronRight className="w-3 h-3" /></span>
                  <Button size="sm" onClick={(e) => { e.stopPropagation(); handleDownload(c); }} disabled={downloadingId === c.id}
                          className="bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs"
                          data-testid={`download-closure-${c.id}`}>
                    {downloadingId === c.id ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <Download className="w-3 h-3 mr-1" />
                    )}
                    PDF
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>

    {selectedId && (
      <ClosureDetailModal
        closureId={selectedId}
        onClose={() => setSelectedId(null)}
        onDownload={handleDownload}
        downloadingId={downloadingId}
      />
    )}
    </>
  );
}

function Kpi({ label, value, accent = 'slate', emphasize = false }) {
  const accents = {
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-900',
    amber: 'bg-amber-50 border-amber-100 text-amber-900',
    slate: 'bg-zinc-50 border-zinc-100 text-slate-700',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${accents[accent]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{label}</div>
      <div className={`font-mono ${emphasize ? 'text-base font-bold' : 'text-sm font-semibold'}`}>
        {fmtEur(value)}
      </div>
    </div>
  );
}


// ─── Detail Modal ───────────────────────────────────────────────
function ClosureDetailModal({ closureId, onClose, onDownload, downloadingId }) {
  const [closure, setClosure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingAttachId, setDownloadingAttachId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/closures/${closureId}`);
        if (alive) setClosure(r.data);
      } catch {
        toast.error('Detail konnte nicht geladen werden');
        onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [closureId, onClose]);

  const handleAttachmentDownload = async (att) => {
    setDownloadingAttachId(att.id);
    try {
      const r = await api.get(`/tresor/documents/${att.id}/download`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: att.content_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.name || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download fehlgeschlagen');
    } finally {
      setDownloadingAttachId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}
         data-testid="closure-detail-modal">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl border border-zinc-200"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-200 flex items-start justify-between bg-emerald-50/40 rounded-t-xl">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Lock className="w-4 h-4 text-slate-600" />
              <h2 className="text-xl font-bold text-slate-900">
                {closure ? `Periodenabschluss ${periodLabel(closure)}` : 'Lade...'}
              </h2>
            </div>
            {closure?.finalized_at && (
              <p className="text-sm text-slate-600">
                Festgeschrieben am {new Date(closure.finalized_at).toLocaleDateString('de-DE')}
                {closure.advisor_name && ` von ${closure.advisor_name}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {closure && (
              <Button size="sm" onClick={() => onDownload(closure)} disabled={downloadingId === closure.id}
                className="bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs"
                data-testid="modal-download-pdf">
                {downloadingId === closure.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
                PDF
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="modal-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {loading || !closure ? (
            <div className="text-center py-10 text-sm text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Lade Details...
            </div>
          ) : (
            <>
              {/* KPIs */}
              <DetailKpiBlock kpis={closure.kpis || {}} />

              {/* Korrekturen */}
              <DetailSection
                title={`Korrekturen durch den Steuerberater (${(closure.corrections || []).length})`}
                icon={Edit3}
                accent="amber">
                {(closure.corrections || []).length === 0 ? (
                  <div className="text-sm text-slate-500 italic">Keine Korrekturen — die ursprünglichen Daten wurden 1:1 übernommen.</div>
                ) : (
                  <div className="space-y-2" data-testid="modal-corrections">
                    {closure.corrections.map((c) => (
                      <div key={c.id} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-xs text-amber-800">
                            {c.entity_type} · {c.field}
                          </span>
                          <span className="text-[10px] text-amber-600">
                            {c.timestamp && new Date(c.timestamp).toLocaleString('de-DE')}
                          </span>
                        </div>
                        <div className="text-xs text-slate-700 mb-1.5">
                          <span className="line-through opacity-60">{String(c.old_value || '—').slice(0, 40)}</span>
                          {' → '}
                          <span className="font-medium">{String(c.new_value || '—').slice(0, 40)}</span>
                        </div>
                        <div className="text-xs text-amber-900 italic">„{c.reason}"</div>
                      </div>
                    ))}
                  </div>
                )}
              </DetailSection>

              {/* Anmerkungen */}
              <DetailSection
                title={`Anmerkungen des Steuerberaters (${(closure.comments || []).length})`}
                icon={MessageSquare}
                accent="blue">
                {(closure.comments || []).length === 0 ? (
                  <div className="text-sm text-slate-500 italic">Keine Anmerkungen.</div>
                ) : (
                  <div className="space-y-2" data-testid="modal-comments">
                    {closure.comments.map((c) => (
                      <div key={c.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-blue-700">
                            {c.author_name || 'SB'}
                            {c.step && <span className="ml-1 text-[10px] text-blue-500 uppercase tracking-wider">[{c.step}]</span>}
                          </span>
                          <span className="text-[10px] text-blue-600">
                            {c.created_at && new Date(c.created_at).toLocaleString('de-DE')}
                          </span>
                        </div>
                        <div className="text-sm text-blue-900">{c.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </DetailSection>

              {/* Anlagen */}
              <DetailSection
                title={`Anlagen (${(closure.attachments || []).length})`}
                icon={Paperclip}
                accent="slate">
                {(closure.attachments || []).length === 0 ? (
                  <div className="text-sm text-slate-500 italic">Keine zusätzlichen Anlagen verknüpft.</div>
                ) : (
                  <div className="space-y-1.5" data-testid="modal-attachments">
                    {closure.attachments.map((a) => (
                      <div key={a.id} className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm text-slate-900 truncate">{a.name}</div>
                            <div className="text-[10px] text-slate-500">
                              {a.folder} · v{a.version} · {a.size_bytes ? `${(a.size_bytes/1024).toFixed(1)} KB` : ''}
                            </div>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => handleAttachmentDownload(a)}
                                disabled={downloadingAttachId === a.id}
                                className="h-7 text-xs shrink-0"
                                data-testid={`modal-attach-dl-${a.id}`}>
                          {downloadingAttachId === a.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Download className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </DetailSection>

              {/* Footer note */}
              <div className="text-xs text-slate-400 italic border-t border-zinc-100 pt-3">
                Dieser Abschluss wurde GoBD-konform festgeschrieben. Spätere Änderungen an den
                Daten der Periode sind nur nach explizitem Aufbrechen mit dokumentierter
                Begründung möglich.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, icon: Icon, accent, children }) {
  const accentColor = {
    amber: 'text-amber-700',
    blue: 'text-blue-700',
    slate: 'text-slate-700',
    emerald: 'text-emerald-700',
  }[accent] || 'text-slate-700';
  return (
    <div>
      <h3 className={`text-sm font-semibold mb-2 flex items-center gap-1.5 ${accentColor}`}>
        <Icon className="w-3.5 h-3.5" />{title}
      </h3>
      {children}
    </div>
  );
}

function DetailKpiBlock({ kpis }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="modal-kpis">
      <DetailKpi label="Einnahmen brutto" sub="netto" value={kpis.income_brutto} subValue={kpis.income_netto} accent="emerald" />
      <DetailKpi label="Ausgaben brutto" sub="netto" value={kpis.expense_brutto} subValue={kpis.expense_netto} accent="slate" />
      <DetailKpi label="Gewinn (netto)" value={kpis.profit_netto} accent="emerald" emphasize />
      <DetailKpi label="USt-Saldo" value={kpis.vat_balance}
                 accent={Number(kpis.vat_balance) >= 0 ? 'amber' : 'emerald'}
                 hint={Number(kpis.vat_balance) >= 0 ? '(Zahllast)' : '(Erstattung)'} />
    </div>
  );
}

function DetailKpi({ label, sub, value, subValue, accent = 'slate', emphasize = false, hint = null }) {
  const accents = {
    emerald: 'bg-emerald-50 border-emerald-200',
    amber: 'bg-amber-50 border-amber-200',
    slate: 'bg-zinc-50 border-zinc-200',
  };
  return (
    <div className={`rounded-lg border ${accents[accent]} px-3 py-2.5`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">{label}</div>
      <div className={`font-mono text-slate-900 ${emphasize ? 'text-lg font-bold' : 'text-sm font-semibold'}`}>
        {fmtEur(value)}
      </div>
      {sub && (
        <div className="text-[10px] text-slate-500 mt-0.5">{sub}: <span className="font-mono">{fmtEur(subValue)}</span></div>
      )}
      {hint && <div className="text-[10px] text-slate-500 mt-0.5 italic">{hint}</div>}
    </div>
  );
}
